import { Link } from 'react-router-dom';
import { Star, Clock, BookOpen, FileText, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, showWishlist = true }) => {
  const { addToCart } = useCart();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [isInWishlist, setIsInWishlist] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return;
    }

    const result = await addToCart(product.product_id, product.product_type);
    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/${product.product_id}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/${product.product_id}`, {}, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setIsInWishlist(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const isCourse = product.product_type === 'course';

  return (
    <Link to={`/product/${product.product_id}`} data-testid={`product-card-${product.product_id}`}>
      <div className="bg-white border border-[#E2E8F0]/60 hover:border-[#2D4A5E]/30 shadow-sm hover:shadow-md transition-all duration-300 group rounded-xl overflow-hidden card-hover">
        {/* Image */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={product.thumbnail || 'https://images.unsplash.com/photo-1634117622592-114e3024ff27?w=800'}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {product.is_featured && (
            <Badge className="absolute top-3 left-3 bg-[#D4AF37] text-[#1A202C] hover:bg-[#C5A028]">
              Featured
            </Badge>
          )}
          {showWishlist && (
            <button
              onClick={handleWishlist}
              className={`absolute top-3 right-3 p-2 rounded-full transition-all ${
                isInWishlist 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/80 text-[#64748B] hover:bg-white hover:text-red-500'
              }`}
              data-testid={`wishlist-btn-${product.product_id}`}
            >
              <Heart className={`w-4 h-4 ${isInWishlist ? 'fill-current' : ''}`} />
            </button>
          )}
          <div className="absolute bottom-3 right-3">
            <Badge variant="secondary" className="bg-white/90 text-[#1A202C]">
              {isCourse ? <BookOpen className="w-3 h-3 mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
              {isCourse ? 'Course' : product.file_type?.toUpperCase() || 'Download'}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">
              {product.category}
            </Badge>
            {isCourse && product.duration && (
              <span className="text-xs text-[#64748B] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {product.duration}
              </span>
            )}
          </div>

          <h3 className="font-semibold text-[#1A202C] group-hover:text-[#2D4A5E] transition-colors line-clamp-2 mb-2">
            {product.title}
          </h3>

          <p className="text-sm text-[#64748B] line-clamp-2 mb-4">
            {product.description}
          </p>

          {/* Rating */}
          {product.review_count > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                <span className="text-sm font-medium">{product.avg_rating?.toFixed(1)}</span>
              </div>
              <span className="text-xs text-[#64748B]">({product.review_count} reviews)</span>
            </div>
          )}

          {/* Price & Action */}
          <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-[#2D4A5E]">
                ${product.price?.toFixed(2)}
              </span>
            </div>
            <Button
              onClick={handleAddToCart}
              className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-5 btn-hover-lift"
              data-testid={`add-to-cart-${product.product_id}`}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
